<script setup lang="ts">
// SRV → Moderation (HRTH-94): every report on this server, with its franking check, state
// and the action recorded. Reports stay until an admin deletes them here.
import { computed, onMounted, ref } from 'vue'
import type { Report, ReportStatus } from '../api/types'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import ReportCard from './ReportCard.vue'

const session = useSession()
const reports = ref<Report[]>([])
const filter = ref<ReportStatus | 'all'>('all')
const shown = computed(() => (filter.value === 'all' ? reports.value : reports.value.filter((r) => r.status === filter.value)))

async function load(): Promise<void> {
  try {
    reports.value = (await session.api.adminReports()).reports
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
onMounted(load)

/** Space names are encrypted: shown where this device has the space, else its id. */
function where(r: Report): string {
  if (r.library) return 'Library file'
  if (!r.space) return 'Direct message'
  return session.spaces.find((s) => s.id === r.space)?.name ?? `Space ${r.space.slice(0, 8)}`
}

/** A reported library file: fetched and decrypted on this device with the key it came with. */
async function openFile(r: Report): Promise<void> {
  const f = r.file
  if (!f) return
  try {
    const { decryptAll } = await import('../lib/attachments')
    const plain = await decryptAll(f, await session.api.adminReportFile(r.id))
    const url = URL.createObjectURL(new Blob([plain], { type: f.mime }))
    const a = document.createElement('a')
    a.href = url
    a.download = (r.library?.path.split('/').pop() || 'reported-file')
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function remove(r: Report): Promise<void> {
  if (!confirm('Delete this report for good? It can’t be recovered.')) return
  try {
    await session.api.deleteReport(r.id)
    await load()
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
</script>

<template>
  <section class="px-6 py-5" data-testid="admin-reports">
    <h2 class="label">Reports · {{ String(reports.length).padStart(2, '0') }}</h2>
    <p class="mono mb-3 text-text-muted">
      Every report made on this server. Each shows what the reporter revealed and whether it was verified: franking for
      messages, and for library files whether the revealed key opens the stored file (download it to review). Space
      moderators handle their space's message reports; direct-message and library reports are yours. Reports stay here
      until you delete them.
    </p>
    <div class="mono mb-3 flex flex-wrap gap-1">
      <button
        v-for="f in (['all', 'open', 'in_review', 'resolved'] as const)"
        :key="f"
        class="border px-2 py-1 uppercase"
        :class="filter === f ? 'border-ink text-text-primary' : 'border-border text-text-muted hover:border-border-strong'"
        :data-testid="`admin-reports-filter-${f}`"
        @click="filter = f"
      >
        {{ f === 'in_review' ? 'In review' : f }}
      </button>
    </div>
    <p v-if="!shown.length" class="mono py-4 text-text-muted">Nothing here.</p>
    <div class="space-y-3">
      <ReportCard v-for="r in shown" :key="r.id" :report="r" :where="where(r)">
        <button v-if="r.file" class="btn-outline h-7 px-2" data-testid="admin-report-file" @click="openFile(r)">Download file</button>
        <button class="btn-ghost h-7 px-2 text-error hover:text-error" data-testid="admin-report-delete" @click="remove(r)">Delete</button>
      </ReportCard>
    </div>
  </section>
</template>
